import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, ChartBarIcon } from '@heroicons/react/24/outline';

const BLOOMS_COLOR = { remember:'#6b7194', understand:'#3b6ef8', apply:'#10c98e', analyze:'#f59e0b', evaluate:'#ef4444', create:'#8b5cf6' };
const DOMAIN_COLOR = { cognitive:'#3b6ef8', affective:'#10c98e', psychomotor:'#f59e0b' };
const curYear = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };

// Sort classes like Grade 1, Grade 2... Grade 10 (natural sort)
const sortClasses = (arr) => [...arr].sort((a, b) => {
  const na = a.name?.match(/\d+/)?.[0]; const nb = b.name?.match(/\d+/)?.[0];
  if (na && nb) return Number(na) - Number(nb);
  return (a.name||'').localeCompare(b.name||'');
});

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ad * { box-sizing:border-box; }
  .ad { font-family:'Sora',sans-serif; color:#1a1d2e; animation:adFade .4s ease both; }
  @keyframes adFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ad-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ad-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .ad-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-blue  { background:#3b6ef8; color:white; }  .btn-blue:hover  { background:#2d5ef0; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .filters { display:flex; gap:10px; margin-bottom:1.4rem; flex-wrap:wrap; align-items:flex-end; }
  .fl { display:flex; flex-direction:column; gap:4px; }
  .fl-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fl-input,.fl-select { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; outline:none; min-width:130px; }
  .fl-input:focus,.fl-select:focus { border-color:#3b6ef8; }
  .overall-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1rem; display:flex; align-items:center; gap:20px; flex-wrap:wrap; }
  .overall-ring { position:relative; width:90px; height:90px; flex-shrink:0; }
  .overall-ring svg { transform:rotate(-90deg); }
  .overall-ring-text { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; }
  .overall-pct { font-size:1.3rem; font-weight:700; font-family:'JetBrains Mono',monospace; color:#0f1123; line-height:1; }
  .overall-lbl { font-size:.6rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-top:2px; }
  .overall-info { flex:1; min-width:0; }
  .overall-title { font-size:.96rem; font-weight:700; color:#0f1123; margin-bottom:4px; }
  .overall-meta  { font-size:.74rem; color:#9094a8; }
  .overall-status { display:inline-flex; align-items:center; gap:5px; font-size:.76rem; font-weight:700; padding:4px 12px; border-radius:99px; margin-top:6px; }
  .overall-status.achieved    { background:#ecfdf5; color:#065f46; }
  .overall-status.not-achieved{ background:#fef2f2; color:#991b1b; }
  .clo-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:1rem; margin-bottom:1rem; }
  .clo-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.2rem; transition:box-shadow .15s; }
  .clo-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.06); }
  .clo-card-top { display:flex; align-items:flex-start; gap:10px; margin-bottom:.8rem; }
  .clo-code-badge { font-family:'JetBrains Mono',monospace; font-size:.76rem; font-weight:700; padding:3px 9px; border-radius:7px; background:#eef2ff; color:#3b6ef8; flex-shrink:0; white-space:nowrap; }
  .clo-desc { font-size:.78rem; color:#1a1d2e; flex:1; line-height:1.5; }
  .clo-pills { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:.9rem; }
  .pill { display:inline-flex; font-size:.64rem; font-weight:600; padding:2px 8px; border-radius:99px; white-space:nowrap; text-transform:capitalize; }
  .att-bar-wrap { margin-bottom:.6rem; }
  .att-bar-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:4px; }
  .att-bar-label { font-size:.72rem; color:#6b7194; }
  .att-bar-pct { font-family:'JetBrains Mono',monospace; font-size:.82rem; font-weight:700; }
  .att-bar { height:8px; background:#f0f2f8; border-radius:99px; overflow:hidden; }
  .att-bar-fill { height:100%; border-radius:99px; transition:width .6s ease; }
  .att-stats { display:flex; gap:12px; font-size:.72rem; color:#9094a8; padding-top:8px; border-top:1px solid #f3f5fb; flex-wrap:wrap; }
  .att-stat { display:flex; flex-direction:column; gap:1px; }
  .att-stat-val { font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; font-size:.8rem; }
  .status-chip { display:inline-flex; align-items:center; gap:4px; font-size:.68rem; font-weight:700; padding:3px 9px; border-radius:99px; }
  .status-chip.achieved    { background:#ecfdf5; color:#10c98e; }
  .status-chip.not-achieved{ background:#fef2f2; color:#ef4444; }
  .status-chip.no-data     { background:#f0f2f7; color:#9094a8; }
  .students-panel { background:white; border-radius:14px; border:1px solid #e8ebf4; overflow:hidden; margin-top:.5rem; }
  .sp-head { padding:10px 14px; background:#fafbff; border-bottom:1px solid #f0f2f8; font-size:.72rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.06em; cursor:pointer; display:flex; align-items:center; justify-content:space-between; }
  .sp-table { width:100%; border-collapse:collapse; }
  .sp-table th { padding:8px 12px; text-align:center !important; font-size:.63rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; background:#fafbff; border-bottom:1px solid #f0f2f8; }
  .sp-table td { padding:8px 12px; font-size:.76rem; border-bottom:1px solid #f3f5fb; text-align:center !important; }
  .sp-table tr:last-child td { border-bottom:none; }
  .ad-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; background:white; border-radius:16px; border:1px solid #e8ebf4; }
  .ad-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  /* Mobile cards for student-breakdown table */
  .sp-cards { display:none; }
  .sp-mrow { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; font-size:.76rem; border-bottom:1px solid #f3f5fb; gap:8px; }
  .sp-mrow:last-child { border-bottom:none; }
  .sp-mname { font-weight:600; }
  @media (max-width:768px) {
    .ad-title{font-size:1.1rem;} .filters{flex-direction:column;} .fl-input,.fl-select{width:100%;min-width:unset;} .clo-grid{grid-template-columns:1fr;}
    .sp-table-wrap { display:none !important; }
    .sp-cards { display:block; }
  }
`;

const Ring = ({ pct=0, size=90, stroke=8, color='#3b6ef8' }) => {
  const r = (size-stroke)/2; const circ = 2*Math.PI*r; const dash = (pct/100)*circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{transform:'rotate(-90deg)'}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0f2f8" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" style={{transition:'stroke-dasharray .6s ease'}}/>
    </svg>
  );
};

export default function AttainmentDashboard({ role='admin' }) {
  const [classes,       setClasses]      = useState([]);
  const [filterClass,   setFilterClass]  = useState('');
  const [filterSection, setFilterSection]= useState('');
  const [filterSubject, setFilterSubject]= useState('');
  const [filterYear,    setFilterYear]   = useState(curYear());
  const [data,          setData]         = useState(null);
  const [loading,       setLoading]      = useState(false);
  const [expandedCLO,   setExpandedCLO]  = useState({});
  const [teacherSubjects, setTeacherSubjects] = useState([]); // for teacher role

  // ── Role-based API base ────────────────────────────────────────────────────
  // Attainment endpoint: admin + coordinator use their own, teacher uses admin
  // (teacher can read attainment but it goes through admin route since data is school-scoped)
  const classesApi    = role === 'admin'       ? '/admin/classes'
                      : role === 'coordinator' ? '/coordinator/classes'
                      : '/teacher/classes';

  const attainmentApi = role === 'coordinator' ? '/coordinator/clos/attainment'
                      : role === 'teacher'      ? '/teacher/clos/attainment'
                      : '/admin/clos/attainment';

  useEffect(() => {
    if (role === 'teacher') {
      // Fetch teacher classes AND profile in parallel
      Promise.all([
        api.get(classesApi),
        api.get('/teacher/profile'),
      ]).then(([classRes, profileRes]) => {
        const rawClasses = classRes.data?.classes || classRes.data || [];
        // Teacher classes may not have subjects — enrich from admin/classes
        // by fetching each class's full detail, OR fetch all classes and match
        const teacherSubs = profileRes.data?.subjects || [];
        setTeacherSubjects(teacherSubs);
        // Fetch full class objects (with subjects) from admin classes endpoint
        // filtered to just the teacher's assigned class IDs
        const classIds = rawClasses.map(c => c._id);
        api.get('/admin/classes').then(r => {
          const allClasses = r.data?.classes || r.data || [];
          // Only keep classes this teacher is assigned to
          const teacherClasses = allClasses.filter(c => classIds.includes(c._id));
          setClasses(sortClasses(teacherClasses.length > 0 ? teacherClasses : rawClasses));
        }).catch(() => {
          // fallback to raw classes if admin endpoint fails
          setClasses(sortClasses(Array.isArray(rawClasses) ? rawClasses : []));
        });
      }).catch(() => {});
    } else {
      api.get(classesApi)
        .then(r => {
          const raw = r.data?.classes || r.data || [];
          setClasses(sortClasses(Array.isArray(raw) ? raw : []));
        })
        .catch(() => {});
    }
  }, [classesApi, role]);

  // Subjects: extracted from class.subjects array (comma-separated strings)
  const getSections = () => classes.find(c => String(c._id) === String(filterClass))?.sections || [];

  const getSubjects = () => {
    const cls = classes.find(c => String(c._id) === String(filterClass));
    if (!cls) return [];
    // Parse subjects from class - stored as array of strings (may be comma-separated)
    const raw = cls.subjects || [];
    const classSubjects = [...new Set(
      raw.flatMap(s => String(s).split(',').map(x => x.trim())).filter(Boolean)
    )].sort();
    // If class has subjects defined, use them for all roles
    if (classSubjects.length > 0) return classSubjects;
    // Fallback for teacher: use profile subjects
    if (role === 'teacher' && teacherSubjects.length > 0) return [...teacherSubjects].sort();
    return [];
  };

  const fetchAttainment = async () => {
    if (!filterClass || !filterSubject || !filterYear)
      return toast.error('Select class, subject and year');
    setLoading(true);
    try {
      const p = new URLSearchParams({ classId: filterClass, subject: filterSubject, academicYear: filterYear });
      if (filterSection) p.append('section', filterSection);
      const { data: res } = await api.get(`${attainmentApi}?${p}`);
      setData(res);
    } catch (err) {
      const msg = err.response?.data?.message;
      if (msg?.includes('OBE is not enabled'))
        toast.error('OBE is not enabled. Admin can enable it in CLO Management → Settings');
      else if (msg?.includes('not enabled for coordinators'))
        toast.error('OBE attainment is not enabled for coordinators');
      else
        toast.error(msg || 'Failed to load attainment');
    } finally { setLoading(false); }
  };

  const handleExportCSV = () => {
    if (!data?.attainment?.length) return;
    const rows = data.attainment.map(a => ({
      'CLO Code':       a.clo.code,
      'Description':    a.clo.description,
      'Domain':         a.clo.domain,
      "Bloom's Level":  a.clo.bloomsLevel,
      'Threshold %':    a.threshold,
      'Total Students': a.totalStudents,
      'Achieved':       a.achievedCount,
      'Attainment %':   a.attainmentPct,
      'Status':         a.status,
      'Assessments':    a.assessmentsCount,
    }));
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${r[h]}"`).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
    a.download = `CLO_Attainment_${filterSubject}_${filterYear}.csv`;
    a.click();
  };

  const overallColor = (pct) => pct >= 80 ? '#10c98e' : pct >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="ad"><style>{css}</style>

      <div className="ad-hdr">
        <div>
          <div className="ad-title">CLO Attainment Dashboard</div>
          <div className="ad-sub">View Course Learning Outcome attainment per class, subject and academic year</div>
        </div>
        {data && <button className="btn btn-ghost" onClick={handleExportCSV}><ArrowDownTrayIcon />Export CSV</button>}
      </div>

      <div className="filters">
        <div className="fl">
          <label className="fl-label">Class *</label>
          <select className="fl-select" value={filterClass}
            onChange={e => { setFilterClass(e.target.value); setFilterSection(''); setFilterSubject(''); setData(null); }}>
            <option value="">Select class…</option>
            {sortByName(classes).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div className="fl">
          <label className="fl-label">Section</label>
          <select className="fl-select" value={filterSection}
            onChange={e => { setFilterSection(e.target.value); setData(null); }}
            disabled={!filterClass}>
            <option value="">All Sections</option>
            {sortAsc(getSections()).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="fl">
          <label className="fl-label">Subject *</label>
          <select className="fl-select" value={filterSubject}
            onChange={e => { setFilterSubject(e.target.value); setData(null); }}
            disabled={!filterClass}>
            <option value="">Select subject…</option>
            {sortAsc(getSubjects()).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="fl">
          <label className="fl-label">Academic Year *</label>
          <input className="fl-input" value={filterYear}
            onChange={e => { setFilterYear(e.target.value); setData(null); }}
            placeholder="2025-2026" style={{minWidth:110}} />
        </div>
        <button className="btn btn-blue" style={{alignSelf:'flex-end'}}
          onClick={fetchAttainment}
          disabled={loading || !filterClass || !filterSubject}>
          <ChartBarIcon />{loading ? 'Calculating…' : 'View Attainment'}
        </button>
      </div>

      {loading && <div className="ad-loading"><div className="spinner"/>Calculating attainment…</div>}

      {!loading && !data && (
        <div className="ad-empty">
          <ChartBarIcon style={{width:32,height:32,margin:'0 auto 12px',display:'block',opacity:.3}}/>
          Select a class, subject and academic year, then click View Attainment.
        </div>
      )}

      {!loading && data && (<>
        <div className="overall-card">
          <div className="overall-ring">
            <Ring pct={data.overallAttainment} size={90} stroke={8} color={overallColor(data.overallAttainment)}/>
            <div className="overall-ring-text">
              <div className="overall-pct" style={{color:overallColor(data.overallAttainment)}}>{data.overallAttainment}%</div>
              <div className="overall-lbl">Overall</div>
            </div>
          </div>
          <div className="overall-info">
            <div className="overall-title">
              {filterSubject} — {classes.find(c=>String(c._id)===String(filterClass))?.name} {filterSection?`(${filterSection})`:''}
            </div>
            <div className="overall-meta">{filterYear} · {data.attainment?.length} CLOs · threshold: {data.threshold}%</div>
            <div className={`overall-status ${data.overallAttainment >= data.threshold ? 'achieved' : 'not-achieved'}`}>
              {data.overallAttainment >= data.threshold ? '✓ Subject attainment achieved' : '✗ Subject attainment not achieved'}
            </div>
          </div>
          <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            {[
              { label:'Achieved',     val: data.attainment?.filter(a=>a.status==='achieved').length||0,     color:'#10c98e' },
              { label:'Not Achieved', val: data.attainment?.filter(a=>a.status==='not-achieved').length||0, color:'#ef4444' },
              { label:'No Data',      val: data.attainment?.filter(a=>a.status==='no-data').length||0,      color:'#9094a8' },
            ].map(s => (
              <div key={s.label} style={{textAlign:'center'}}>
                <div style={{fontSize:'1.3rem',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:s.color}}>{s.val}</div>
                <div style={{fontSize:'.66rem',color:'#9094a8',fontWeight:600}}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {data.attainment?.length === 0 ? (
          <div className="ad-empty">No CLOs defined for this combination. Add CLOs in CLO Management first.</div>
        ) : (
          <div className="clo-grid">
            {data.attainment.map((a) => {
              const barColor = a.attainmentPct >= 80 ? '#10c98e' : a.attainmentPct >= 60 ? '#f59e0b' : '#ef4444';
              const isExpanded = !!expandedCLO[a.clo._id];
              return (
                <div key={a.clo._id} className="clo-card">
                  <div className="clo-card-top">
                    <span className="clo-code-badge">{a.clo.code}</span>
                    <span className="clo-desc">{a.clo.description}</span>
                  </div>
                  <div className="clo-pills">
                    <span className="pill" style={{background:`${DOMAIN_COLOR[a.clo.domain]||'#3b6ef8'}18`,color:DOMAIN_COLOR[a.clo.domain]||'#3b6ef8'}}>{a.clo.domain}</span>
                    <span className="pill" style={{background:`${BLOOMS_COLOR[a.clo.bloomsLevel]||'#6b7194'}18`,color:BLOOMS_COLOR[a.clo.bloomsLevel]||'#6b7194'}}>{a.clo.bloomsLevel}</span>
                    <span className={`status-chip ${a.status}`}>
                      {a.status==='achieved'?'✓ Achieved':a.status==='not-achieved'?'✗ Not Achieved':'— No Data'}
                    </span>
                  </div>
                  <div className="att-bar-wrap">
                    <div className="att-bar-row">
                      <span className="att-bar-label">Attainment</span>
                      <span className="att-bar-pct" style={{color:barColor}}>{a.attainmentPct}%</span>
                    </div>
                    <div className="att-bar">
                      <div className="att-bar-fill" style={{width:`${a.attainmentPct}%`,background:barColor}}/>
                    </div>
                    <div style={{fontSize:'.67rem',color:'#b0b5cc',marginTop:3}}>Threshold: {a.threshold}%</div>
                  </div>
                  <div className="att-stats">
                    <div className="att-stat"><span className="att-stat-val">{a.totalStudents}</span><span>Students</span></div>
                    <div className="att-stat"><span className="att-stat-val" style={{color:'#10c98e'}}>{a.achievedCount}</span><span>Achieved</span></div>
                    <div className="att-stat"><span className="att-stat-val" style={{color:'#ef4444'}}>{a.totalStudents-a.achievedCount}</span><span>Not Achieved</span></div>
                    <div className="att-stat"><span className="att-stat-val" style={{color:'#9094a8'}}>{a.assessmentsCount}</span><span>Assessments</span></div>
                  </div>
                  {a.studentScores?.length > 0 && (
                    <div className="students-panel" style={{marginTop:10}}>
                      <div className="sp-head" onClick={() => setExpandedCLO(p => ({...p,[a.clo._id]:!p[a.clo._id]}))}>
                        Student Breakdown {isExpanded ? '▲' : '▼'}
                      </div>
                      {isExpanded && (
                        <>
                          <div className="sp-table-wrap" style={{overflowX:'auto'}}>
                            <table className="sp-table">
                              <thead><tr><th>Student</th><th>Score %</th><th>Status</th></tr></thead>
                              <tbody>
                                {a.studentScores.sort((x,y)=>y.pct-x.pct).map(s => (
                                  <tr key={s.studentId}>
                                    <td style={{fontWeight:600}}>{s.name||'—'}</td>
                                    <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,color:s.pct>=a.threshold?'#10c98e':'#ef4444'}}>{s.pct}%</td>
                                    <td><span className={`status-chip ${s.achieved?'achieved':'not-achieved'}`}>{s.achieved?'✓':'✗'}</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="sp-cards">
                            {a.studentScores.sort((x,y)=>y.pct-x.pct).map(s => (
                              <div key={s.studentId} className="sp-mrow">
                                <span className="sp-mname">{s.name||'—'}</span>
                                <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,color:s.pct>=a.threshold?'#10c98e':'#ef4444'}}>{s.pct}%</span>
                                <span className={`status-chip ${s.achieved?'achieved':'not-achieved'}`}>{s.achieved?'✓ Achieved':'✗ Not Achieved'}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>)}
    </div>
  );
}